import { units } from '@dynatrace-sdk/units';

import { getAxisFormatterWithTimeFallback } from './get-axis-formatter-with-time-fallback.js';
import {
  getMagnifierLabel,
  MagnifierFormatter,
} from '../../../../core/components/crosshair/utils/get-magnifier-label.js';
import {
  FormatterWithPrecision,
  TimeFormatter,
} from '../../../../core/types/formatter.js';
import { defaultUnitFormatter } from '../../../../core/utils/defaultUnitFormatter.js';
import type { FormatCategory } from '../../../hooks/useFormatCategory.js';
import {
  AxisIdToFormatter,
  XYChartAxisInternal,
  XYChartVisibleAxesConfig,
  XYChartVisibleAxesScales,
} from '../../../types/xy-chart-internals.js';

type AXIS = 'left' | 'right' | 'bottom' | 'top';
type AXES = AXIS[];
type Formatters = [AXIS, MagnifierFormatter][];
type LabelsAccumulator = {
  left: string | undefined;
  right: string | undefined;
  bottom: string | undefined;
  top: string | undefined;
};
type GetFormatter = (
  axis: AXIS,
) => FormatterWithPrecision | TimeFormatter | undefined;
type GetLabel = (
  axis: AxisWithFormatter[0],
  formatter: AxisWithFormatter[1],
) => string | undefined;

type AxisWithFormatter = Formatters[number];

const coordinates = {
  top: 'x',
  bottom: 'x',
  left: 'y',
  right: 'y',
} as const;

const defaultLabels = () => ({
  left: undefined,
  right: undefined,
  bottom: undefined,
  top: undefined,
});

type PartialAxesConfig = {
  [key in keyof XYChartVisibleAxesConfig]:
    | Pick<
        XYChartAxisInternal,
        'id' | 'formatter' | 'type' | 'unit' | 'isRelative'
      >
    | undefined;
};

export type ChartCrosshairDataTransformer = (args: {
  axesScale: XYChartVisibleAxesScales;
  axesConfig: PartialAxesConfig;
  axesIdToFormatter: AxisIdToFormatter;
  width: number;
  axes?: AXES;
  formatCategory?: FormatCategory;
}) => CrosshairLabelsGetter;

export type CrosshairLabelsGetter = (args: {
  position: { x: number; y: number };
  pinned: boolean;
}) => LabelsAccumulator;

const accumulateFormatters =
  (getFormatter: GetFormatter) => (acc: Formatters, axis: AXIS) => {
    const formatter = getFormatter(axis);

    if (formatter) {
      acc.push([axis, formatter]);
    }
    return acc;
  };

const accumulateLabels =
  (getLabel: GetLabel) =>
  (acc: LabelsAccumulator, axisWithFormatter: AxisWithFormatter) => {
    const [axis, formatter] = axisWithFormatter;
    const label = getLabel(axis, formatter);

    if (label) {
      acc[axis] = label;
    }

    return acc;
  };

export const chartCrosshairDataTransformer: ChartCrosshairDataTransformer = ({
  axesScale,
  axesConfig,
  axesIdToFormatter,
  width,
  axes = ['left', 'right', 'bottom', 'top'],
  formatCategory,
}) => {
  const getFormatter: GetFormatter = (axis: AXIS) => {
    const axisConfig = axesConfig[axis];
    const scale = axesScale[axis];
    if (axisConfig) {
      if (axisConfig.isRelative) {
        return defaultUnitFormatter(units.percentage.percent);
      }
      return getAxisFormatterWithTimeFallback({
        axisConfig,
        axisId: axisConfig.id,
        scale,
        axesIdToFormatter,
        width,
      });
    }
  };

  const formatters = axes.reduce(
    accumulateFormatters(getFormatter),
    [] as Formatters,
  );

  return ({ position, pinned }) => {
    if (pinned) {
      return defaultLabels();
    }

    const getLabel: GetLabel = (axis, formatter) => {
      const raw = getMagnifierLabel(
        axesConfig[axis]?.unit,
        coordinates[axis],
        formatter,
        position,
        axesScale[axis],
      );
      if (raw === null || !formatCategory) {
        return raw;
      }
      return formatCategory(axis, raw) ?? raw;
    };

    return formatters.reduce(accumulateLabels(getLabel), defaultLabels());
  };
};
